/**
 * The bridge between an id in the document and an id on the drawn page.
 *
 * They are the same string, which is the whole point: the exporter writes each event's and
 * each bar's own id into the MusicXML it hands the engine, the engine copies the attribute
 * onto the element it draws, and a click on that element therefore names a node of the
 * document without any coordinate arithmetic. The alternative -- matching a click against a
 * table of rectangles -- has to be rebuilt on every relayout and is wrong at every overlap.
 *
 * One asymmetry survives. A chord is a single event holding several pitches, but is drawn as
 * several note heads, and an XML id may name only one element; so the heads after the first
 * carry the event's id with a `.1`, `.2` suffix. Reading an id back therefore means dropping
 * anything from the first separator on, which is what {@link elementEventId} does.
 */

import { idKind } from "../document/ids";
import type { EventId, MeasureId } from "../document/ids";

/** Separates an event's id from the index of the chord tone drawn with it. */
export const CHORD_SEPARATOR = ".";

/** The id the engine should be given for one pitch of an event. */
export function elementIdFor(id: EventId, pitchIndex = 0): string {
  return pitchIndex === 0 ? id : `${id}${CHORD_SEPARATOR}${pitchIndex}`;
}

/** The part of a drawn element's id before the chord suffix, if it has one. */
function stem(elementId: string): string {
  const cut = elementId.indexOf(CHORD_SEPARATOR);
  return cut === -1 ? elementId : elementId.slice(0, cut);
}

/**
 * The event a drawn element belongs to, or `null` when it is not one of ours.
 *
 * Engines mint ids of their own for everything they draw -- stems, beams, slurs, the page
 * itself -- so most elements under the cursor answer `null` here. That is the expected
 * answer and not a failure: a click walks up from the deepest element until one of them
 * names a node.
 */
export function elementEventId(elementId: string): EventId | null {
  const base = stem(elementId);
  return idKind(base) === "event" ? (base as EventId) : null;
}

/** The bar a drawn element belongs to, or `null` when it is not one of ours. */
export function elementMeasureId(elementId: string): MeasureId | null {
  return idKind(elementId) === "measure" ? (elementId as MeasureId) : null;
}

/** Which pitch of a chord an element was drawn for; `0` for a single note or a rest. */
export function elementPitchIndex(elementId: string): number {
  const cut = elementId.indexOf(CHORD_SEPARATOR);
  if (cut === -1) return 0;
  const index = Number(elementId.slice(cut + 1));
  return Number.isInteger(index) && index > 0 ? index : 0;
}

/**
 * Every id this package stamped that appears in a piece of the engine's output.
 *
 * The engine's markup is read as text rather than parsed, because the only thing wanted
 * from it is which of our ids it mentions, and a page of engraved music is a large document
 * to build a tree for in order to ask one question about it.
 */
export function idsWithin(svg: string): readonly string[] {
  const found: string[] = [];
  for (const match of svg.matchAll(/\bid="([^"]+)"/g)) {
    const id = match[1];
    if (id === undefined) continue;
    const kind = idKind(stem(id));
    if (kind === "event" || kind === "measure") found.push(id);
  }
  return found;
}
